import { Component, input, OnInit } from '@angular/core';

import { EChartsOption } from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import {
  correlationBarChartOptions,
  dateHistogramChartOptions,
  gaugeChartOptions,
  GaugeValue,
  histogramChartOptions,
  pieChartOptions,
} from '../../../helpers/chart-options';
import { DataType, TableFeatureResponse } from '../../../models/api-responses';
import { ChartCard } from './chart-card/chart-card';

interface ChartData {
  chartOptions: EChartsOption;
  title: string;
  subtitle: string;
  hasData: boolean;
}

@Component({
  selector: 'app-data-set-dashboard',
  imports: [NgxEchartsModule, ChartCard],
  templateUrl: './data-set-dashboard.html',
  styleUrl: './data-set-dashboard.scss',
})
export class DataSetDashboard implements OnInit {
  dataSetFeatures = input<TableFeatureResponse[]>([]);
  chartsData: ChartData[] = [];

  ngOnInit(): void {
    this.createCharts();
  }

  private createCharts(): void {
    if (this.dataSetFeatures() != null && this.dataSetFeatures().length > 0) {
      this.dataSetFeatures().forEach((feature) => {
        if (feature.profile == null) {
          return;
        }
        this.createGenericChart(feature);
        this.createGaugeChart(feature);
        this.createCorrelationChart(feature);
      });
    }
  }

  private createGaugeChart(feature: TableFeatureResponse): void {
    let chartOptions: EChartsOption = {};
    const data = this.createGaugeData(feature);

    if (
      (feature.dataType === DataType.Float ||
        feature.dataType === DataType.Int) &&
      data.length > 2
    ) {
      chartOptions = gaugeChartOptions(feature, data);

      this.chartsData.push({
        title: feature.name as string,
        subtitle:
          `${feature.entityType}:${feature.dataType}` +
          (feature.description != null ? ` - ${feature.description}` : ''),
        chartOptions: chartOptions as EChartsOption,
        hasData: data != null && data.length > 0,
      });
    }
  }

  private createGaugeData(feature: TableFeatureResponse): GaugeValue[] {
    const gaugeData: GaugeValue[] = [];
    if (
      feature.dataType === DataType.Float ||
      feature.dataType === DataType.Int
    ) {
      if (feature.profile.minValue != null) {
        gaugeData.push({
          value: feature.profile.minValue,
          name: 'Min',
          itemStyle: { color: '#FAC858' },
          title: {
            offsetCenter: ['-80%', '80%'],
          },
          detail: {
            offsetCenter: ['-80%', '95%'],
          },
        });
      }

      if (feature.profile.mean != null) {
        gaugeData.push({
          value: feature.profile.mean,
          name: 'Mean',
          itemStyle: { color: '#91CC75' },
          title: {
            offsetCenter: ['0%', '80%'],
          },
          detail: {
            offsetCenter: ['0%', '95%'],
          },
        });
      }

      if (feature.profile.maxValue != null) {
        gaugeData.push({
          value: feature.profile.maxValue,
          name: 'Max',
          itemStyle: { color: '#5470C6' },
          title: {
            offsetCenter: ['80%', '80%'],
          },
          detail: {
            offsetCenter: ['80%', '95%'],
          },
        });
      }
    }

    return gaugeData;
  }

  private createGenericChart(feature: TableFeatureResponse): void {
    const valueCounts = JSON.parse(feature.profile.valueCounts ?? '[]');
    let chartOptions: EChartsOption = {};
    if (
      feature.dataType === DataType.Float ||
      feature.dataType === DataType.Int
    ) {
      chartOptions = histogramChartOptions(feature);
    } else if (
      feature.dataType === DataType.Category ||
      feature.dataType === DataType.Bool ||
      feature.dataType === DataType.String
    ) {
      chartOptions = pieChartOptions(feature);
    } else if (
      feature.dataType === DataType.Date ||
      feature.dataType === DataType.TimeStamp
    ) {
      chartOptions = dateHistogramChartOptions(feature);
    }

    this.chartsData.push({
      title: feature.name as string,
      subtitle:
        `${feature.entityType}:${feature.dataType}` +
        (feature.description != null ? ` - ${feature.description}` : ''),
      chartOptions: chartOptions as EChartsOption,
      hasData: valueCounts != null && valueCounts.length > 0,
    });
  }

  private createCorrelationChart(feature: TableFeatureResponse): void {
    if (feature.profile.correlationMatrix != null) {
      const chartOptions = correlationBarChartOptions(feature);
      this.chartsData.push({
        title: feature.name as string,
        subtitle:
          `${feature.entityType}:${feature.dataType}` +
          (feature.description != null ? ` - ${feature.description}` : ''),
        chartOptions: chartOptions as EChartsOption,
        hasData: true,
      });
    }
  }
}
